/**
 * Settings Screen - Volume sliders, language grid, stats, save/reset
 * Skeleton build + patch rendering pattern
 */
import { formatNumber, formatTime } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { SUPPORTED_LOCALES, setLocale, getCurrentLocale, type Locale } from '../i18n/index.js';
import { getBgmVolume, getSfxVolume, setBgmVolume, setSfxVolume } from '../sound.js';
import { onRender, getState, setInnerHTML, showModal, showToast, markScreenDirty, requestRender } from './renderer.js';

const SAVE_KEY = 'crypto_clicker_save';

let container: HTMLDivElement | null = null;

const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  ja: '日本語',
  ko: '한국어',
  de: 'Deutsch',
  es: 'Espanol',
  pt: 'Portugues',
  fr: 'Francais',
  'zh-TW': '繁體中文',
  th: 'ไทย',
};

export function initSettingsScreen() {
  const content = document.getElementById('screen-content');
  if (!content) return;

  container = document.createElement('div');
  container.id = 'settings-screen';
  container.className = 'screen-panel';
  content.appendChild(container);

  onRender(renderSettings);
}

function renderSettings() {
  if (!container) return;
  const st = getState();

  const bgmVol = getBgmVolume();
  const sfxVol = getSfxVolume();
  const curLocale = getCurrentLocale();

  let html = '';

  // Sound section
  html += `<div class="card">
    <div class="card-title">${t('settings_sound')}</div>
    <div class="slider-row">
      <label class="slider-label">${t('settings_bgm')}</label>
      <input type="range" class="slider" id="bgm-slider" min="0" max="100" value="${bgmVol}" />
      <span class="slider-val" id="bgm-val">${bgmVol}</span>
    </div>
    <div class="slider-row">
      <label class="slider-label">${t('settings_sfx')}</label>
      <input type="range" class="slider" id="sfx-slider" min="0" max="100" value="${sfxVol}" />
      <span class="slider-val" id="sfx-val">${sfxVol}</span>
    </div>
  </div>`;

  // Language section
  html += `<div class="card">
    <div class="card-title">${t('settings_language')}</div>
    <div class="lang-grid">`;
  for (const loc of SUPPORTED_LOCALES) {
    const active = loc === curLocale;
    html += `<button class="lang-btn${active ? ' lang-active' : ''}" data-locale="${loc}">${LOCALE_LABELS[loc] || loc}</button>`;
  }
  html += `</div></div>`;

  // Stats section
  html += `<div class="card">
    <div class="card-title">${t('settings_stats')}</div>
    <div class="stats-list">
      <div class="stat-row"><span>${t('stats_total_earned')}</span><span class="text-gold">${formatNumber(st.statistics.totalEarned)}</span></div>
      <div class="stat-row"><span>${t('stats_total_clicks')}</span><span>${formatNumber(st.statistics.totalClicks, 0)}</span></div>
      <div class="stat-row"><span>${t('stats_max_combo')}</span><span class="text-orange">${st.combo.maxCombo}</span></div>
      <div class="stat-row"><span>${t('stats_golden_clicks')}</span><span class="text-gold">${st.statistics.totalGoldenClicks}</span></div>
      <div class="stat-row"><span>${t('stats_prestige_count')}</span><span class="text-cyan">${st.prestige.count}${t('prestige_count_unit')}</span></div>
      <div class="stat-row"><span>${t('stats_prestige2_count')}</span><span class="text-purple">${st.prestige2.count}${t('prestige_count_unit')}</span></div>
      <div class="stat-row"><span>${t('stats_play_time')}</span><span>${formatTime(st.statistics.playTime)}</span></div>
    </div>
  </div>`;

  // Data section
  html += `<div class="card">
    <div class="card-title">${t('settings_data')}</div>
    <button class="act-btn act-btn-blue" id="btn-save">${t('settings_save')}</button>
    <button class="act-btn act-btn-red" id="btn-reset">${t('settings_reset')}</button>
  </div>`;

  if (setInnerHTML(container, html)) {
    // Slider events
    const bgmSlider = container.querySelector('#bgm-slider') as HTMLInputElement;
    const sfxSlider = container.querySelector('#sfx-slider') as HTMLInputElement;
    const bgmVal = container.querySelector('#bgm-val');
    const sfxVal = container.querySelector('#sfx-val');

    if (bgmSlider) {
      bgmSlider.addEventListener('input', () => {
        const v = parseInt(bgmSlider.value);
        setBgmVolume(v);
        if (bgmVal) bgmVal.textContent = String(v);
      });
    }
    if (sfxSlider) {
      sfxSlider.addEventListener('input', () => {
        const v = parseInt(sfxSlider.value);
        setSfxVolume(v);
        if (sfxVal) sfxVal.textContent = String(v);
      });
    }

    // Language buttons
    container.querySelectorAll('[data-locale]').forEach(btn => {
      btn.addEventListener('click', () => {
        const loc = (btn as HTMLElement).dataset.locale as Locale;
        setLocale(loc);
        markScreenDirty();
        requestRender();
        // Full re-render to update all UI text
        location.reload();
      });
    });

    // Save button (strip _outcome for anti-cheat)
    container.querySelector('#btn-save')?.addEventListener('click', () => {
      try {
        const currentSt = getState();
        const saveData = {
          ...currentSt,
          lastSaveTimestamp: Date.now(),
          darkPool: currentSt.darkPool ? { ...currentSt.darkPool, _outcome: null } : currentSt.darkPool,
        };
        localStorage.setItem(SAVE_KEY, JSON.stringify(saveData));
        showToast(t('toast_saved'));
      } catch { /* ignore */ }
    });

    // Reset button
    container.querySelector('#btn-reset')?.addEventListener('click', () => {
      showModal(
        t('settings_reset_title'),
        `<div class="modal-detail">${t('settings_reset_confirm')}</div>`,
        [
          { label: t('btn_cancel'), color: 'var(--card-border)', onClick: () => {} },
          {
            label: t('settings_reset'), color: 'var(--red)', onClick: () => {
              localStorage.removeItem(SAVE_KEY);
              location.reload();
            }
          },
        ]
      );
    });
  }
}
